import { api } from './api.js';

/**
 * Cache of the user's events, shared between the Events page, the sidebar and Home's Events
 * panel. All three draw on every navigation, so fetching in each of them would mean several
 * API calls per page change.
 *
 * Whether an event is *live* changes with the clock rather than with anything the user does,
 * so the cache also ages out on its own -- a party that starts at seven should appear in the
 * menu without the user happening to navigate at the right moment.
 */

const TTL_MS = 60_000;

let cache = null;
let fetchedAt = 0;
let inflight = null;
/** Bumped by clear(), so a request still in flight for the previous account can't write back. */
let generation = 0;
const listeners = new Set();

/**
 * Forgets the list. It belongs to whoever was signed in: without this, signing out and in as
 * someone else in the same tab showed the previous account's events in the menu.
 */
export function clear() {
    generation += 1;
    cache = null;
    fetchedAt = 0;
    inflight = null;
}

window.addEventListener('auth:logout', clear);
window.addEventListener('auth:login', clear);

export function onChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

function notify() {
    for (const fn of listeners) {
        try {
            fn(cache ?? []);
        } catch (err) {
            console.error('event listener failed', err);
        }
    }
}

export async function getEvents({ force = false } = {}) {
    const fresh = cache && Date.now() - fetchedAt < TTL_MS;
    if (fresh && !force) return cache;
    if (inflight) return inflight;

    const gen = generation;
    inflight = (async () => {
        try {
            const res = await api.listEvents();
            if (gen !== generation) return [];
            cache = res.events ?? [];
            fetchedAt = Date.now();
            return cache;
        } catch {
            // A failed list must not break the page it decorates.
            if (gen !== generation) return [];
            cache = cache ?? [];
            return cache;
        } finally {
            if (gen === generation) inflight = null;
        }
    })();

    const result = await inflight;
    notify();
    return result;
}

/** Anything that creates, edits or deletes an event calls this so the menu keeps up. */
export async function refresh() {
    return getEvents({ force: true });
}

export function cachedEvents() {
    return cache;
}

/**
 * `is_active` is computed server-side, but re-checked here against the clock: a cached list
 * up to a minute old would otherwise keep showing an event as live after it ended.
 */
export function isLive(ev, now = Date.now()) {
    return now >= new Date(ev.starts_at).getTime() && now <= new Date(ev.ends_at).getTime();
}

/** How many events the sidebar shows before deferring to the Events page. */
const SIDEBAR_LIMIT = 5;

/**
 * What the sidebar should list: every event, not just the live ones, because an event page
 * stays available for good once it exists.
 *
 * Ordered live-first and then newest-first, and capped at five. A family will accumulate
 * events for years, and a menu that grows without limit stops being a menu -- so the tail
 * becomes a "More..." line pointing at the Events page, which is the real full list. Nothing
 * is hidden, only deferred one click.
 */
export function sidebarEvents() {
    if (!cache) return null;
    const now = Date.now();

    const ordered = [...cache].sort((a, b) => {
        const liveDelta = Number(isLive(b, now)) - Number(isLive(a, now));
        if (liveDelta !== 0) return liveDelta;
        return new Date(b.starts_at) - new Date(a.starts_at);
    });

    return {
        shown: ordered.slice(0, SIDEBAR_LIMIT),
        hidden: Math.max(0, ordered.length - SIDEBAR_LIMIT),
    };
}

/** An organiser opens the admin page; someone who joined opens the guest page. */
export function eventHref(ev) {
    return ev.role === 'guest' ? `/e/${ev.slug}` : `/events/${ev.id}`;
}
